<script lang="ts" setup>
import { computed, ref } from 'vue'
import { formatPrice } from '@/helper/tools.ts'
import IconSaleSet from '@/components/icons/IconSaleSet.vue'
import { Article } from '@/services/models/Article.ts'
import { CharacteristicType } from '@/helper/Types.ts'
import { SalesPackageCharacteristic } from '@/services/models/SalesPackageCharacteristic.ts'

const props = withDefaults(
  defineProps<{
    salePackage: Article
    salePackageNumber: number
    isSelected: boolean
  }>(),
  {
    isSelected: () => false
  }
)
defineEmits<{
  (e: 'onSelectSalePackage'): void
}>()

const showBasic = ref(false)
const showAccessories = ref(true)

const salesPackageArticles =
  props.salePackage.details?.getProductCharacteristic<SalesPackageCharacteristic>(CharacteristicType.SALES_PACKAGE).articles
    .map(a => new Article(a)) || []

const salesPackageBasicComponents = salesPackageArticles
  .filter(article => article.details?.getProductCharacteristic(CharacteristicType.HEAT_PUMP) ||
    article.details?.getProductCharacteristic(CharacteristicType.APPLIANCE_INTERFACE) ||
    article.details?.getProductCharacteristic(CharacteristicType.HYDRAULIC_MODULE) ||
    article.details?.getProductCharacteristic(CharacteristicType.HYDRAULIC_TOWER))

const salesPackageAccessoriesComponents = salesPackageArticles
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.HEAT_PUMP))
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.APPLIANCE_INTERFACE))
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.HYDRAULIC_MODULE))
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.HYDRAULIC_TOWER))

const basicComponentsSubtitle = computed(() => {
  return salesPackageBasicComponents.length > 0
    ? salesPackageBasicComponents.map(c => c.details?.name).filter(Boolean).join(' + ')
    : ''
})

function toggleShowBasic() {
  showBasic.value = !showBasic.value
}

function toggleShowAccessories() {
  showAccessories.value = !showAccessories.value
}

</script>

<template>
  <div class="card" :class="{ 'is-selected': isSelected }" @click.exact="$emit('onSelectSalePackage')">
    <div class="sale-set__header" :class="{ 'header--selected': isSelected }">
      <div class="sale-set__header__left">
        <div class="sale-set__header__icon">
          <IconSaleSet />
        </div>

        <div class="sale-set__header__text">
          <p class="sale-set__header__title">
            {{ $t('step_sale_set.heat_pump_set') }} {{ salePackageNumber }}
          </p>
          <p class="sale-set__header__subtitle">
            {{ basicComponentsSubtitle }}
          </p>
        </div>
      </div>

      <button
        class="sale-set__header__select"
        :class="{ 'sale-set__header__select__selected': isSelected }"
        type="button"
        @click.exact="$emit('onSelectSalePackage')"
      >
        <span> {{ isSelected ? $t('step_sale_set.selected') : $t('step_sale_set.select') }}</span>
      </button>
    </div>
    <div :class="['sale-set__components', { 'is--open': showBasic }]">
      <div class="sale-set__components__header">
        <span>{{ $t('step_sale_set.basic_components') }}</span>

        <div>
          <button
            :class="{ 'is--open': showBasic}"
            class="btn is--raw fTable__details-btn"
            type="button"
            @click.prevent.stop="toggleShowBasic"
          >
            <span>{{ $t('step_components.table_details') }}</span>
            <i :class="showBasic ? 'ic-chevron-up' : 'ic-chevron-down'" />
          </button>
        </div>
      </div>
      <template v-if="showBasic">
        <div
          v-for="product in salesPackageBasicComponents"
          :key="product.id"
          class="sale-set__components__card"
        >
          <div v-if="product.details?.pictureUrl !== 'none'" class="sale-set__components__card__img">
            <img :src="product.details?.pictureUrl" :alt="product.details?.name" loading="lazy">
          </div>
          <div v-else class=" sale-set__components__card__img fallback-bg" />
          <div class="sale-set__components__card__info__left">
            <div class="sale-set__components__card__name">
              {{ product.details?.name }}
            </div>
            <div class="sale-set__components__card__article">
              {{ $t('step_sale_set.article_number') }}: {{ product.details?.orderNumber }}
            </div>
          </div>
          <div class="sale-set__components__card__quantity">
            × {{ product.quantity || 1 }}
          </div>
        </div>
      </template>
    </div>
    <div v-if="salesPackageAccessoriesComponents.length > 0" :class="['sale-set__components', { 'is--open': showAccessories }]">
      <div class="sale-set__components__header">
        <span>{{ $t('step_sale_set.accessories_components') }}</span>

        <div>
          <button
            :class="{ 'is--open': showAccessories}"
            class="btn is--raw fTable__details-btn"
            type="button"
            @click.prevent.stop="toggleShowAccessories"
          >
            <span>{{ $t('step_components.table_details') }}</span>
            <i :class="showAccessories ? 'ic-chevron-up' : 'ic-chevron-down'" />
          </button>
        </div>
      </div>
      <template v-if="showAccessories">
        <div
          v-for="product in salesPackageAccessoriesComponents"
          :key="product.id"
          class="sale-set__components__card"
        >
          <div v-if="product.details?.pictureUrl !== 'none'" class="sale-set__components__card__img">
            <img :src="product.details?.pictureUrl" :alt="product.details?.name" loading="lazy">
          </div>
          <div v-else class=" sale-set__components__card__img fallback-bg" />
          <div class="sale-set__components__card__info__left">
            <div class="sale-set__components__card__name">
              {{ product.details?.name }}
            </div>
            <div class="sale-set__components__card__article">
              {{ $t('step_sale_set.article_number') }}: {{ product.details?.orderNumber }}
            </div>
          </div>
          <div class="sale-set__components__card__quantity">
            × {{ product.quantity || 1 }}
          </div>
        </div>
      </template>
    </div>

    <div class="sale-set__price">
      <div class="sale-set__price-label">
        {{ $t('step_sale_set.price') }}
      </div>
      <div class="sale-set__price-value">
        {{ formatPrice(props.salePackage.details?.price) }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card {
  border: 2px solid $saleSetCardBorderColor;
}

.fallback-bg {
  background: radial-gradient($imageGradient);
}

.is-selected {
  .theme-va & {
    border: 2px solid $vaPrimaryColor;
  }

  .theme-sd & {
    border: 2px solid $sdPrimaryColor;
  }
}

.header--selected {
  .theme-va & {
    border-bottom: 2px solid $vaPrimaryColor;
  }

  .theme-sd & {
    border-bottom: 2px solid $sdPrimaryColor;
  }
}

.sale-set {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 3rem 1.5rem 3rem;
    background: $saleSetCardColor;

    @include themify($themes) {
      color: themed('primaryColor');
    }

    .theme-va .card.is-selected & {
      background: $vaPrimaryLightBackgroundColor;
    }

    .theme-sd .card.is-selected & {
      background: $sdPrimaryLightBackgroundColor;
      border-radius: 10px;
    }

    &__left {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    &__icon {
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      .theme-va & {
        background: $vaSaleSetIconColor;
      }

      .theme-sd & {
        background: $sdSaleSetIconColor;
      }

    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &__title {
      font-weight: bold;
      margin: 0;
    }

    &__subtitle {
      font-size: 0.875rem;
      margin: 0;
    }

    &__select {
      border-radius: 1.1875rem;
      background-color: transparent;

      .theme-va & {
        color: $vaPrimaryColor;
        border: 1px solid $vaPrimaryColor;
      }

      .theme-sd & {
        color: $sdPrimaryColor;
        border: 1px solid $sdPrimaryColor;
      }

      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.625rem;

      padding: 0.5rem 2rem;
      justify-content: center;

      &__selected {
        .theme-va & {
          background-color: $vaPrimaryColor;
          color: white;
        }

        .theme-sd & {
          background-color: $sdPrimaryColor;
          color: white;
        }
      }

    }
  }

  &__components {
    display: flex;
    padding: 0.75rem 1.5rem 1rem 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    align-self: stretch;
    border-radius: 0.75rem;
    border: 1px solid $borderColorLight;
    background: $saleSetCardColor;
    transition: background 0.3s ease;
    margin: 1.5rem 3rem;

    &.is--open {
      background: white;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem;
      width: 100%;
    }

    &__card {
      display: flex;
      padding: 0.75rem 1rem;
      align-items: center;
      gap: 0;
      align-self: stretch;
      border-radius: 0.75rem;
      background: $saleSetCardColor;

      &__img {
        width: 4rem;
        height: 4rem;
        flex-shrink: 0;
        border-radius: 8px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      &__info {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        padding-left: 1rem;

        &__left {
          display: flex;
          flex-direction: column;
          padding: 0.25rem 1rem;
        }
      }

      &__name {
        color: $textColor;
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
      }

      &__article {
        color: $textColorLight;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5rem;
      }

      &__quantity {
        margin-left: auto;

        display: flex;
        padding: 0.25rem 1rem;
        align-items: flex-start;
        gap: 0.25rem;
        border-radius: 1rem;

        .theme-va & {
          border: 1px solid $vaPrimaryColor;
          background: $vaPrimaryLightBackgroundColor;
          color: $vaPrimaryColor;
        }

        .theme-sd & {
          border: 1px solid $sdPrimaryColor;
          background: $sdPrimaryLightBackgroundColor;
          color: $sdPrimaryColor;
        }
      }
    }
  }

  &__price {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    margin: 1.5rem 3rem;

    &-label {
      font-weight: 400;
      line-height: 1.5rem;
      font-style: normal;
    }

    &-value {
      font-weight: 600;
      line-height: 1.875rem;
      font-style: normal;
      font-size: 1.5rem;
    }
  }
}

</style>
